import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import { z } from "zod";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** The consent channels a church can record, in the order the staff dashboard shows them. */
export const CHANNELS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "Text messages" },
  { value: "phone", label: "Phone calls" },
  { value: "mail", label: "Postal mail" },
] as const;
export type Channel = (typeof CHANNELS)[number]["value"];

const contactSchema = z.object({
  id: z.string(),
  method: z.enum(["email", "phone"]),
  value: z.string(),
  label: z.string().nullable(),
  isPrimary: z.boolean(),
});

const preferenceSchema = z.object({
  channel: z.enum(["email", "sms", "phone", "mail"]),
  optedIn: z.boolean(),
  source: z.string(),
  recordedAt: z.string(),
});

const householdMemberSchema = z.object({
  personId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable(),
  role: z.enum(["adult", "child", "other"]),
  isPrimaryContact: z.boolean(),
  isMe: z.boolean(),
});

/**
 * What `my_profile` returns (ADR 0045): one person's own record, resolved from their login. Parsed on the way in so a
 * shape the function did not promise fails loudly here rather than as an undefined somewhere in the screen.
 */
export const myProfileSchema = z.object({
  person: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    preferredName: z.string().nullable(),
    birthdate: z.string().nullable(),
    campus: z.object({ id: z.string(), name: z.string() }).nullable(),
  }),
  contacts: z.array(contactSchema),
  preferences: z.array(preferenceSchema),
  household: z
    .object({
      id: z.string(),
      name: z.string(),
      address: z.object({
        line1: z.string().optional(),
        line2: z.string().optional(),
        locality: z.string().optional(),
        region: z.string().optional(),
        postalCode: z.string().optional(),
      }),
      members: z.array(householdMemberSchema),
    })
    .nullable(),
});
export type MyProfile = z.infer<typeof myProfileSchema>;
export type MyContact = z.infer<typeof contactSchema>;
export type MyPreference = z.infer<typeof preferenceSchema>;
export type MyHouseholdMember = z.infer<typeof householdMemberSchema>;

/**
 * The member's own record. `42501` means the login lacks portal access in this church; `P0002` means the church has not
 * linked this login to a person yet — a real state the screen has to explain, not an error to retry.
 */
export function useMyProfile() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["my-profile", tenantId],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async (): Promise<MyProfile> => {
      const { data, error } = await supabase.rpc("my_profile", { p_tenant_id: tenantId ?? "" });
      if (error) throw error;
      return myProfileSchema.parse(data);
    },
  });
}

function useProfileInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return () => client.invalidateQueries({ queryKey: ["my-profile", tenantId] });
}

export interface MyDetailsInput {
  readonly firstName: string;
  readonly lastName: string;
  readonly preferredName: string;
}

export function useUpdateMyDetails() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useProfileInvalidation();
  return useMutation({
    mutationFn: async (input: MyDetailsInput) => {
      const { error } = await supabase.rpc("update_my_details", {
        p_tenant_id: tenantId ?? "",
        p_first_name: input.firstName,
        p_last_name: input.lastName,
        ...(input.preferredName.trim() === "" ? {} : { p_preferred_name: input.preferredName }),
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export interface MyContactInput {
  readonly method: "email" | "phone";
  readonly value: string;
  readonly label: string;
  readonly isPrimary: boolean;
}

export function useSetMyContact() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useProfileInvalidation();
  return useMutation({
    mutationFn: async (input: MyContactInput) => {
      const { error } = await supabase.rpc("set_my_contact", {
        p_tenant_id: tenantId ?? "",
        p_method: input.method,
        p_value: input.value,
        ...(input.label.trim() === "" ? {} : { p_label: input.label }),
        p_is_primary: input.isPrimary,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useRemoveMyContact() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useProfileInvalidation();
  return useMutation({
    mutationFn: async (contactId: string) => {
      const { error } = await supabase.rpc("remove_my_contact", {
        p_tenant_id: tenantId ?? "",
        p_contact_id: contactId,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useSetMyPreference() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useProfileInvalidation();
  return useMutation({
    mutationFn: async (input: { channel: Channel; optedIn: boolean }) => {
      const { error } = await supabase.rpc("set_my_preference", {
        p_tenant_id: tenantId ?? "",
        p_channel: input.channel,
        p_opted_in: input.optedIn,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}
